import { useEffect, useState } from 'react';
import { Plus, Trash2, Edit, TestTube } from 'lucide-react';

interface SMTPConfig {
  id: string;
  name: string;
  host: string;
  port: number;
  secure: boolean;
  auth_user: string;
  auth_pass: string;
  from_email: string;
  from_name: string;
  is_active: boolean;
  is_default: boolean;
  max_per_hour: number;
}

export default function SMTPConfigs() {
  const [configs, setConfigs] = useState<SMTPConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port: 587,
    secure: false,
    auth_user: '',
    auth_pass: '',
    from_email: '',
    from_name: '',
    is_default: false,
    max_per_hour: 100
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/smtp/configs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setConfigs(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar configs SMTP:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const url = editingId ? `${apiUrl}/api/smtp/configs/${editingId}` : `${apiUrl}/api/smtp/configs`;
      const method = editingId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      
      if (data.success) {
        alert(editingId ? 'Configuração atualizada!' : 'Configuração criada!');
        setShowModal(false);
        setEditingId(null);
        loadConfigs();
        resetForm();
      } else {
        alert(`Erro: ${data.error}`);
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar configuração');
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/smtp/test/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert('✅ Conexão SMTP OK!');
      } else {
        alert(`❌ Erro: ${data.error}`);
      }
    } catch (error) {
      console.error('Erro ao testar:', error);
      alert('Erro ao testar conexão');
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar esta configuração?')) return;
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/smtp/configs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Configuração deletada!');
        loadConfigs();
      }
    } catch (error) {
      console.error('Erro ao deletar:', error);
    }
  };

  const handleEdit = (config: SMTPConfig) => {
    setEditingId(config.id);
    setFormData({
      name: config.name,
      host: config.host,
      port: config.port,
      secure: config.secure,
      auth_user: config.auth_user,
      auth_pass: '', // Não preencher senha por segurança
      from_email: config.from_email,
      from_name: config.from_name,
      is_default: config.is_default,
      max_per_hour: config.max_per_hour
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      host: '',
      port: 587,
      secure: false,
      auth_user: '',
      auth_pass: '',
      from_email: '',
      from_name: '',
      is_default: false,
      max_per_hour: 100
    });
  };

  if (loading) return <div className="p-8">Carregando...</div>;

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Configurações SMTP</h1>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-600"
        >
          <Plus size={20} /> Novo SMTP
        </button>
      </div>

      <div className="grid gap-4">
        {configs.map(config => (
          <div key={config.id} className="bg-white p-4 rounded shadow">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-bold text-lg">{config.name}</h3>
                  {config.is_default && (
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Padrão</span>
                  )}
                  {!config.is_active && (
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">Inativo</span>
                  )}
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Host:</strong> {config.host}:{config.port} {config.secure ? '(SSL)' : '(TLS)'}</p>
                  <p><strong>Usuário:</strong> {config.auth_user}</p>
                  <p><strong>De:</strong> {config.from_name} &lt;{config.from_email}&gt;</p>
                  <p><strong>Limite:</strong> {config.max_per_hour} emails/hora</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleTest(config.id)}
                  disabled={testingId === config.id}
                  className="p-2 text-blue-500 hover:bg-blue-50 rounded"
                  title="Testar conexão"
                >
                  <TestTube size={20} />
                </button>
                <button
                  onClick={() => handleEdit(config)}
                  className="p-2 text-yellow-500 hover:bg-yellow-50 rounded"
                >
                  <Edit size={20} />
                </button>
                <button
                  onClick={() => handleDelete(config.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingId ? 'Editar' : 'Nova'} Configuração SMTP
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  required
                  placeholder="Ex: Gmail Principal"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Host SMTP</label>
                  <input
                    type="text"
                    value={formData.host}
                    onChange={e => setFormData({...formData, host: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    required
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Porta</label>
                  <input
                    type="number"
                    value={formData.port}
                    onChange={e => setFormData({...formData, port: Number(e.target.value)})}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.secure}
                    onChange={e => setFormData({...formData, secure: e.target.checked})}
                  />
                  <span className="text-sm">SSL (porta 465)</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Usuário</label>
                <input
                  type="text"
                  value={formData.auth_user}
                  onChange={e => setFormData({...formData, auth_user: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  required
                  placeholder="seu@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Senha {editingId && '(deixe em branco para manter)'}
                </label>
                <input
                  type="password"
                  value={formData.auth_pass}
                  onChange={e => setFormData({...formData, auth_pass: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  required={!editingId}
                  placeholder="senha ou API key"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email Remetente</label>
                  <input
                    type="email"
                    value={formData.from_email}
                    onChange={e => setFormData({...formData, from_email: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nome Remetente</label>
                  <input
                    type="text"
                    value={formData.from_name}
                    onChange={e => setFormData({...formData, from_name: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Seu Nome/Empresa"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Limite por Hora</label>
                <input
                  type="number"
                  value={formData.max_per_hour}
                  onChange={e => setFormData({...formData, max_per_hour: Number(e.target.value)})}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={e => setFormData({...formData, is_default: e.target.checked})}
                  />
                  <span className="text-sm">Definir como padrão</span>
                </label>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingId(null); }}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  {editingId ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
